import {
  useState,
  useContext,
  ChangeEvent,
  useMemo,
  createContext,
  PropsWithChildren,
  useEffect,
  useCallback,
  useRef
} from "react";
import { TranslationService } from "../../../services/TranslationService";
import TableContext from "../../task/TableContext";
import RangedDatepicker from "./DateRangePicker";
import { formatDate, formatDateDigits } from "../../../utils/FormatUtils";
import {
  AutocompleteClient,
  AutocompleteGroup,
  MultiselectEditor,
  TagsDropdownEditor,
  TextEditor,
  TextEditorNumber,
  NumberInput
} from "./Editors";
import FilterService, {
  DELETED_USERS_FILTER_VALUE,
  FilterCollection,
  FilterDefinition,
  FilterOption
} from "../../../services/FilterService";
import CompanyService, { DataType } from "../../../services/CompanyService";
import AdvancedFilterService, {
  Filter,
  FilterActivityTypeWithDate,
  FilterLine
} from "../../../services/AdvancedFilterService";
import Collapsable from "../bootstrap/Collapsable";
import Dropdown, { DropdownProps } from "./Dropdown";
import ActivityService from "../../../services/ActivityService";
import { useLocation } from "react-router-dom";
import TooltipComponent from "../TooltipComponent";
import StoredFiltersService, {
  STORED_FILTER_NAME_MAX_LENGTH
} from "../../../services/StoredFiltersService";
import {
  StoredFilterItem,
  StoredFiltersResponse
} from "../../../entities/Filter/StoredFiltersResponse";
import FloatingPanelService from "../FloatingPanel";
import { FloatingPanelFooter } from "./FloatingPanelFooter";
import ToastContext, { ToastService } from "../bootstrap/Toast";
import ModalService from "../bootstrap/modal/Modal";
import { RequiredManager, ValidationMessage } from "../RequieredManager";
import { getError } from "../../../utils/RequestUtils";

type AdvancedFiltersParams = {
  page: FilterCollection;
  onFilterApply?: (filters: string[]) => void;
  defaultValue?: string;
  onAppliedFiltersCount?: (filterCount: number) => void;
  filterOptions?: FilterOption[];
  allowClosing?: boolean;
  removeApplyButtton?: boolean;
  autoApply?: boolean;
  reportFilters?: number;
  useEntityPrefix?: boolean;
  standalone?: boolean;
};

export enum TYPE_REPORT {
  Activity = 0, //historial de gestion
  IO = 1, //facturas
  Person, //cliente
  IOActivity, //gestiones or factura
  GroupAging, //reporte agrupado aging
  Payment, //reporte de pagos
  Inbox,
  Task,
  ReportReceivablesSummary, //analisis de cobranzas
  ReportCollection, //analisis de recupero
  Tag, //reporte de etiquetas
  GroupAgingGrouping = 11 //agrupaciones guardadas del reporte aging agrupado
}

export type ReportId = keyof typeof TYPE_REPORT;

const AdvancedFilters = ({
  page,
  onFilterApply = undefined,
  defaultValue = undefined,
  allowClosing = true,
  filterOptions,
  removeApplyButtton = false,
  autoApply = false,
  useEntityPrefix = false,
  standalone = false
}: AdvancedFiltersParams) => {
  const filtersOptions = filterOptions ?? FilterService.GetFiltersForPage(page);
  const {
    setFiltersCount,
    filtersCount,
    setShow,
    show,
    selectedFilterSaved,
    setSelectedFilterSaved,
    reportId,
    filtersSaved,
    setIsSaving,
    isSaving,
    setFiltersSaved,
    setFilterSavedByPage,
    firstSelection,
    setFirstSelection
  } = useContext(AdvancedFiltersContext);

  const [filters, setFilters] = useState<FilterLine[]>(() =>
    getDefaultFilters(defaultValue, filtersOptions)
  );
  const { showToast } = useContext(ToastContext);
  const { applyFilters } = useContext(TableContext);

  const canSaveFilter = !firstSelection && filters[0].value;

  useEffect(() => {
    setFiltersCount(filters.filter((x) => !x.isEmpty()).length);
    // Safety: We only need it the first time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      defaultValue !== undefined &&
      defaultValue !== null &&
      defaultValue !== ""
    ) {
      const newFilters = getDefaultFilters(defaultValue, filtersOptions);

      newFilters.forEach((x, index) => {
        if (filters[index]) {
          x.id = filters[index].id;
        }
        if (!x.isEmpty()) {
          x.encodedFilter = AdvancedFilterService.filterToString(x, {
            useEntityPrefix
          });
        }
      });

      const validFilters = newFilters.filter((x) => !x.isEmpty());
      setFiltersCount(validFilters.length);
      setFilters(newFilters);

      if (validFilters.length > 0) {
        _applyFilters(validFilters);
      }
    } else if (defaultValue === null || defaultValue === "") {
      setFilters([new FilterLine()]);
      setFiltersCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const _applyFilters = (filters: FilterLine[]) => {
    const filter = {
      extraFilters: filters
        .map((x) => x.encodedFilter)
        .filter((x) => x !== undefined && x.length > 0)
    };
    setFiltersCount(filter.extraFilters.length);
    if (onFilterApply) {
      onFilterApply(filter.extraFilters);
    } else {
      applyFilters(filter);
    }
  };

  const deleteFilter = (filter: FilterLine | string) => {
    const id = typeof filter === "string" ? filter : filter.id;
    const newFilters = [...filters.filter((x) => x.id !== id)];
    if (
      filters.filter((x) => !x.isEmpty()).length !==
      newFilters.filter((x) => !x.isEmpty()).length
    ) {
    }
    setFilters(newFilters);
    if (newFilters.length === 0) {
      setFilters([new FilterLine()]);
      if (allowClosing) {
        setShow(false);
      }
    }
    if (newFilters.length !== filtersCount || autoApply) {
      _applyFilters(newFilters);
    }
  };

  const addFilter = () => {
    setFilters([...filters, new FilterLine()]);
  };

  // Añade esta referencia cerca de tus otros hooks (useState, useContext)
  const autoApplyTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateFilter = (item: FilterLine, value: Filter) => {
    // setSelectedFilterSaved(-1);
    Object.assign(item, value);
    setFilters([...filters]);

    if (autoApply) {
      // Cancelamos el filtro anterior si el usuario sigue tecleando
      if (autoApplyTimeout.current) {
        clearTimeout(autoApplyTimeout.current);
      }
      // Ejecutamos el filtro solo tras 500ms de inactividad
      autoApplyTimeout.current = setTimeout(() => {
        applyFilter([...filters]);
      }, 500);
    }
  };

  const cleanFilters = () => {
    setFirstSelection(true);
    setSelectedFilterSaved(-1);
    setFilters([new FilterLine()]);
    if (filtersCount !== 0) {
      applyFilter([]);
    }
  };

  const getSavedFiltersNew = useCallback(
    async (reload?: boolean, newId?: number) => {
      try {
        setIsSaving(true);
        const response = await StoredFiltersService.getList({
          report: reportId!
        });
        if (response instanceof Error) {
          console.error("Error");
        } else {
          setFiltersSaved(response);
          const list =
            response.list.map((el) => ({
              Value: el.Name,
              Id: el.StoredFilterID
            })) || [];
          list.unshift({
            Value: TranslationService.translate.MyFilters,
            Id: -1
          });
          await setFilterSavedByPage([...list]);
          newId && (await setSelectedFilterSaved(newId));
          await setFirstSelection(true);
          await setIsSaving(false);
        }
      } catch (error) {
      } finally {
        showToast(
          TranslationService.translate.DataUpdatedSuccessfully,
          "",
          "success"
        );
      }
    },
    [
      reportId,
      setFilterSavedByPage,
      setFiltersSaved,
      setFirstSelection,
      setIsSaving,
      setSelectedFilterSaved,
      showToast
    ]
  );

  const saveFilter = async () => {
    try {
      setIsSaving(true);
      const filterSavedChanged = filtersSaved?.list.find(
        (el) => el.StoredFilterID === selectedFilterSaved
      );

      const filter = filters
        .map((x) =>
          AdvancedFilterService.filterToString(x, { useEntityPrefix })
        )
        .filter((x) => x !== undefined && x.length > 0);

      const filtersAux = {
        ...filterSavedChanged,
        Report: reportId,
        Filter: AdvancedFilterService.filterStringsToQueryString(filter)
      };
      await StoredFiltersService.update({
        item: {
          Filter: filtersAux.Filter,
          StoredFilterID: filtersAux.StoredFilterID
        }
      }).finally(() =>
        showToast(
          TranslationService.translate.DataUpdatedSuccessfully,
          "",
          "success"
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const saveFilterAs = () => {
    const filter = filters
      .map((x) => AdvancedFilterService.filterToString(x, { useEntityPrefix }))
      .filter((x) => x !== undefined && x.length > 0);

    const filtersAux = {
      Report: reportId,
      Filter: AdvancedFilterService.filterStringsToQueryString(filter)
    };
    if (parseInt(reportId) >= 0)
      FloatingPanelService.showPanel({
        title: TranslationService.translate.Save,
        children: (
          <StoredFilters
            filter={filtersAux}
            reportId={reportId}
            getSavedFilters={getSavedFiltersNew}
            isNew
          />
        ),
        height: 350,
        width: 600,
        position: "center"
      });
  };

  const applyFilter = (orig?: FilterLine[]) => {
    const newFilters = [
      ...(orig ?? filters).filter((x) => Number(x.kind) !== -1)
    ];
    newFilters.forEach((x) => {
      x.encodedFilter = AdvancedFilterService.filterToString(x, {
        useEntityPrefix
      });
    });
    _applyFilters(newFilters);
  };

  useEffect(() => {
    const filterSavedByPage = CompanyService.getStoredFilters()?.find(
      (f) => f.StoredFilterID === selectedFilterSaved
    );
    if (filterSavedByPage && filtersOptions) {
      const filters = AdvancedFilterService.parseFilters(
        filterSavedByPage.Filter,
        filtersOptions
      );
      _applyFilters(filters);
      setFilters(filters);
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterSaved, filtersOptions]);

  const nonEmptyFilters = filters.filter((x) => !x.isEmpty());
  const nonEmptyFiltersEncoded = filters.some(
    (x) => x.encodedFilter.length > 0
  );

  const filterInputs = (
    <div className="pb-3">
      <div
        className="filters-container"
        style={{ minHeight: standalone ? 120 : show ? 120 : 0 }}
      >
        {filters.map((item, index) => (
          <FilterComponent
            key={item.id}
            page={page}
            qtyFilters={filters.length}
            deleteFilter={() => deleteFilter(item)}
            updateFilter={(value) => updateFilter(item, value)}
            options={filtersOptions}
            defaultValue={item.isEmpty() ? undefined : item}
          />
        ))}
        <div className="d-flex justify-content-between">
          <div className="addRow align-content-center">
            <button className="btn btn-link p-0" onClick={addFilter}>
              <i className="fa-light fa-plus me-2"></i>
              {TranslationService.translate.Add}
            </button>
          </div>
          {isSaving ? (
            <button className="btn btn-link text-granite-gray" disabled>
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            </button>
          ) : (
            <>
              {filters.length > 0 && !removeApplyButtton && (
                <div className="addRow d-flex gap-12">
                  {parseInt(reportId) >= 0 ? (
                    <>
                      {canSaveFilter ? (
                        <TooltipComponent
                          title={TranslationService.translate.Save}
                        >
                          <button
                            className="btn btn-link "
                            onClick={saveFilter}
                          >
                            <i className="fa-light fa-save fa-lg"></i>
                          </button>
                        </TooltipComponent>
                      ) : null}

                      {nonEmptyFiltersEncoded ? (
                        <TooltipComponent
                          title={TranslationService.translate.SaveAs}
                        >
                          <button
                            className="btn btn-link "
                            onClick={saveFilterAs}
                          >
                            <i className="fas fa-fw  fa-save fa-lg"></i>
                          </button>
                        </TooltipComponent>
                      ) : null}
                    </>
                  ) : null}
                  <TooltipComponent title={TranslationService.translate.Clear}>
                    <button className="btn btn-link " onClick={cleanFilters}>
                      <i className="fa-light fa-eraser fa-lg"></i>
                    </button>
                  </TooltipComponent>

                  <TooltipComponent
                    title={TranslationService.translate.ApplyFilters}
                  >
                    <button
                      className="btn btn-link "
                      onClick={() => {
                        const emptyMultiselect = filters.find(
                          (x) => x.kind === DataType.Multiselect && !x.value
                        );
                        if (emptyMultiselect) {
                          showToast(
                            TranslationService.translate.SelectAtLeastOneOption,
                            "",
                            "danger"
                          );
                          return;
                        }
                        selectedFilterSaved &&
                          selectedFilterSaved > -1 &&
                          setFirstSelection(false);
                        applyFilter();
                      }}
                    >
                      <i className="fa-light fa-check fa-lg"></i>
                    </button>
                  </TooltipComponent>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {!standalone && (
        <Collapsable show={!show}>
          {nonEmptyFilters.length > 0 && (
            <div className="pb-3" style={{ minHeight: show ? 120 : 0 }}>
              <div className="chipsRowFilter m-0">
                {nonEmptyFilters.map((x) => (
                  <span className="chipItem" key={x.id}>
                    {AdvancedFilterService.FriendlyName(x)}{" "}
                    <span
                      className="pointer ps-1"
                      onClick={() => deleteFilter(x)}
                    >
                      {" "}
                      ×{" "}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </Collapsable>
      )}
      {standalone ? (
        filterInputs
      ) : (
        <Collapsable show={show}>{filterInputs}</Collapsable>
      )}
    </div>
  );
};

export type SavedFilter = {
  name: string;
  filters: string[];
};

function getDefaultFilters(
  defaultValue: string | undefined,
  filtersOptions: FilterOption[]
) {
  const filters = AdvancedFilterService.parseFilters(
    defaultValue,
    filtersOptions
  );
  if (filters.length === 0) {
    filters.push(new FilterLine());
  }
  return filters;
}

const FilterComponent = (props: {
  page: FilterCollection;
  options: FilterOption[];
  deleteFilter: () => void;
  updateFilter: (filter: Filter) => void;
  qtyFilters: number;
  defaultValue?: Filter;
}) => {
  const [filter, setFilter] = useState(props.defaultValue);
  const { translate } = TranslationService;

  // Actualizar el filtro cuando cambia defaultValue (por ejemplo, al cargar desde URL)
  useEffect(() => {
    setFilter(props.defaultValue);
  }, [props.defaultValue]);

  const onFilterChange = (property: string) => (value: string) => {
    const newFilter = filter!;
    Object.assign(newFilter, { [property]: value });
    setFilter({ ...newFilter });
    updateFilterValue({ ...newFilter });
  };

  const onChangeValueInput = (event: ChangeEvent<HTMLInputElement>) =>
    onChangeValue(event.target.value);
  const onChangeValueInputText = (value: string) => onChangeValue(value);
  const onChangeValue = onFilterChange("value");

  // Date filters accept EITHER an absolute date/range OR a relative day count, never
  // both. Each setter clears the other field so the filter can only ever mean one thing.
  const onChangeDateValue = (value: string) => {
    const newFilter = filter!;
    Object.assign(newFilter, { value, relativeDays: "" });
    setFilter({ ...newFilter });
    updateFilterValue({ ...newFilter });
  };

  const onChangeRelativeDays = (days: string, operator: number) => {
    const newFilter = filter!;
    Object.assign(newFilter, { relativeDays: days, operator, value: "" });
    setFilter({ ...newFilter });
    updateFilterValue({ ...newFilter });
  };

  const handleFilter = (definition: undefined | FilterDefinition) => {
    if (definition === undefined) {
      setFilter(undefined);
      return;
    }
    const filter: Filter = { kind: definition.Type, definition: definition };

    setDefaultValues(filter);
    updateFilterValue(filter);
    setFilter({ ...filter, operator: 0 });
    function setDefaultValues(filter: Filter) {
      switch (filter.kind) {
        case DataType.List:
          //filter.value = filter.definition.AdditionalDefinitionItems[0].Value;
          filter.value = undefined;
          break;
        case DataType.YesNo:
          filter.value = "0";
          break;
        case DataType.Date:
          filter.value = formatDateDigits(new Date(), "ja").replaceAll("/", "");
          break;
        case DataType.Priority:
          filter.value = "1";
          break;
        default:
          break;
      }
    }
  };

  const updateFilterValue = (filter: Filter) => {
    props.updateFilter(filter);
  };

  const formatDefinitionKey = (definition: FilterDefinition) => {
    return definition.Entity + "%" + definition.Field;
  };

  const mainDropdownItems: DropdownProps<FilterDefinition>["items"] =
    props.options.flatMap((x) => [
      { text: x.title, isTitle: true },
      ...x.definitions.map((y) => ({
        text: y.Name,
        value: y,
        key: formatDefinitionKey(y)
      }))
    ]);

  const values = filter?.value
    ?.split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  // Para segmentos (Field === "-7"), cargar dinámicamente desde CompanyService
  const mappedItems = useMemo(() => {
    const isSegmentField = String(filter?.definition.Field) === "-7";
    if (isSegmentField) {
      // Cargar segmentos dinámicamente
      const segments = CompanyService.getSegments();
      const mapped = segments.map((segment) => ({
        label: segment.Value,
        value: String(segment.Id)
      }));
      return mapped;
    } else {
      // Para otros tipos de lista, usar AdditionalDefinitionItems
      const items =
        filter?.definition.AdditionalDefinitionItems.map((x) => ({
          label: x.Label,
          value: x.Value
        })) ?? [];
      return items;
    }
  }, [filter?.definition.Field, filter?.definition.AdditionalDefinitionItems]);

  const selectedValues = mappedItems?.filter((x) => values?.includes(x.value));
  const activityTypeFilterItems = useMemo(
    () =>
      ActivityService.ActivityTypeFilterItems.map((x) => ({
        Label: TranslationService.getTranslation(x.Label),
        Value: x.Value
      })),
    []
  );

  const typeFilters = useMemo(
    () =>
      ActivityService.TypeFilterItems.map((x) => ({
        Label: TranslationService.getTranslation(x.Label),
        Value: x.Value
      })),
    []
  );

  const setFilterCombo = (filter: Filter) => {
    setFilter(filter);
    updateFilterValue(filter);
  };

  return (
    <div className="filter-row">
      <div className="row ms-0 me-1">
        <div className="col-md-4">
          <Dropdown
            value={filter?.definition}
            onChange={handleFilter}
            optionLabel={translate.Select}
            items={mainDropdownItems}
            showSearchFilter={true}
          />
        </div>
        {!filter && (
          <>
            <div className="col-md-4">
              <Dropdown onChange={() => undefined} items={[]} />
            </div>
            <div className="col-md-4">
              <input className="form-control" />
            </div>
          </>
        )}
        {filter && DataType.List === filter.kind && (
          <>
            <SelectOperations
              operators={AdvancedFilterService.getListOperatorsForPage(
                props.page
              )}
              value={filter.operator}
              filter={filter}
              setFilter={setFilterCombo}
            />
            {filter.operator !== 7 && filter.operator !== 8 && (
              <MultipleSelectOperations
                selectedValues={selectedValues}
                keyValues={mappedItems!}
                filter={filter}
                setFilter={setFilterCombo}
              />
            )}
          </>
        )}
        {filter && DataType.ListNoOperator === filter.kind && (
          <>
            <SelectValue
              value={filter.value}
              keyValues={filter.definition.AdditionalDefinitionItems}
              onChangeValue={onChangeValue}
            />
          </>
        )}
        {filter && DataType.Date === filter.kind && (
          <DateTextboxValue
            defaultValue={filter.value}
            relativeDays={filter.relativeDays}
            operator={filter.operator}
            onChangeValue={onChangeDateValue}
            onChangeRelativeDays={onChangeRelativeDays}
          />
        )}
        {filter && filter.kind === DataType.Currency && (
          <>
            <SelectOperations
              operators={AdvancedFilterService.numericOperator}
              value={filter!.operator}
              filter={filter}
              setFilter={setFilterCombo}
            />
            <div className="col-auto px-1">
              <TextEditorNumber
                onChange={onChangeValueInputText}
                defaultValue={filter.value}
                withBox={true}
                boxLeft={true}
                boxText={CompanyService.GetDefaultCurrencySymbol()}
              />
            </div>
          </>
        )}
        {filter &&
          [DataType.Number, DataType.DaysOld].includes(filter.kind) && (
            <>
              <SelectOperations
                operators={AdvancedFilterService.numericOperator}
                value={filter!.operator}
                filter={filter}
                setFilter={setFilterCombo}
              />
              <div className="col-auto px-1">
                <TextEditorNumber
                  onChange={onChangeValueInputText}
                  defaultValue={filter.value}
                />
              </div>
              {filter.kind === DataType.DaysOld && (
                <div className="col-auto px-1">
                  <input
                    type="text"
                    readOnly
                    className="form-control-plaintext"
                    value={translate.DaysOld}
                  />
                </div>
              )}
            </>
          )}
        {filter && DataType.DayCompare === filter.kind && (
          <>
            <SelectOperations
              operators={AdvancedFilterService.numericOperator}
              value={filter!.operator}
              filter={filter}
              setFilter={setFilterCombo}
            />
            <div className="col-md-2">
              <TextEditorNumber
                onChange={onChangeValueInputText}
                defaultValue={filter.value}
              />
            </div>
            <div className="col-md-2">
              <input
                type="text"
                readOnly
                className="form-control-plaintext"
                value={translate.Days}
              />
            </div>
          </>
        )}
        {filter && DataType.Tags === filter.kind && (
          <div className="col-md-4">
            <TagsDropdownEditor
              onChange={onChangeValue}
              defaultValue={filter.value}
              forceMultiple={true}
            />
          </div>
        )}
        {filter && DataType.ActivityType === filter.kind && (
          <div className="col-md-4">
            <MultiselectEditor
              callback={(x) => onChangeValue(x?.join(",") ?? "")}
              value={filter.value
                ?.split(",")
                .map((x) =>
                  filter.definition.AdditionalDefinitionItems.find(
                    (y) => y.Value === x
                  )!
                )
                .filter((x) => x)
                .map((x) => ({ value: x.Value, label: x.Label }))}
              items={filter.definition.AdditionalDefinitionItems.map((x) => ({
                value: x.Value,
                label: x.Label
              })).sort((a, b) => a.label.localeCompare(b.label))}
            />
          </div>
        )}
        {filter && DataType.UserMultiSelect === filter.kind && (
          <div className="col-md-4">
            <MultiselectEditor
              callback={(x) => {
                const values = x ?? [];
                let next = values;
                if (
                  values.includes(DELETED_USERS_FILTER_VALUE) &&
                  values.length > 1
                ) {
                  const wasDeletedSelected = (
                    filter.value?.split(",") ?? []
                  ).includes(DELETED_USERS_FILTER_VALUE);
                  // "Usuarios eliminados" es excluyente con el resto: se queda
                  // el que se acaba de agregar (el otro se descarta)
                  next = wasDeletedSelected
                    ? values.filter((v) => v !== DELETED_USERS_FILTER_VALUE)
                    : [DELETED_USERS_FILTER_VALUE];
                }
                onChangeValue(next.join(","));
              }}
              value={filter.value
                ?.split(",")
                .map((x) =>
                  filter.definition.AdditionalDefinitionItems.find(
                    (y) => y.Value === x
                  )!
                )
                .filter((x) => x)
                .map((x) => ({ value: x.Value, label: x.Label }))}
              items={filter.definition.AdditionalDefinitionItems.map((x) => ({
                value: x.Value,
                label: x.Label
              })).sort((a, b) => {
                // "Usuarios eliminados" siempre primero, igual que en v1
                if (a.value === DELETED_USERS_FILTER_VALUE) return -1;
                if (b.value === DELETED_USERS_FILTER_VALUE) return 1;
                return a.label.localeCompare(b.label);
              })}
            />
          </div>
        )}
        {filter && DataType.Multiselect === filter.kind && (
          <div className="col-md-4">
            <MultiselectEditor
              callback={(x) => onChangeValue(x?.join(",") ?? "")}
              value={filter.value
                ?.split(",")
                .map((x) =>
                  filter.definition.AdditionalDefinitionItems.find(
                    (y) => y.Value === x
                  )!
                )
                .filter((x) => x)
                .map((x) => ({ value: x.Value, label: x.Label }))}
              items={filter.definition.AdditionalDefinitionItems.map((x) => ({
                value: x.Value,
                label: x.Label
              }))}
            />
          </div>
        )}
        {filter && DataType.User === filter.kind && (
          <SelectValue
            value={filter.value}
            keyValues={filter.definition.AdditionalDefinitionItems.map((x) => ({
              Label: x.Label,
              Value: x.Value
            }))}
            onChangeValue={onChangeValue}
          />
        )}
        {filter &&
          [DataType.Client, DataType.ClientGroup].includes(filter.kind) && (
            <div className="col-md-4">
              <AutocompleteClient
                onChange={onChangeValue}
                defaultValue={filter.value}
              />
            </div>
          )}
        {filter && [DataType.ClientGroup].includes(filter.kind) && (
          <div className="col row align-items-center">
            <div className="col-auto">{CompanyService.getGroupName()}:</div>
            <div className="col-md-4">
              <AutocompleteGroup
                onChange={(x) => onFilterChange("operator")(x?.value ?? "")}
                clientId={filter.value ?? ""}
              />
            </div>
          </div>
        )}
        {filter && DataType.Priority === filter.kind && (
          <div className="col-md-4">
            <MultiselectEditor
              callback={(x) => onChangeValue(x?.join(",") ?? "")}
              value={filter.value
                ?.split(",")
                .map((x) => x.trim())
                .filter((x) => x.length > 0)
                .map((x) => ({ value: x, label: x }))}
              items={Array.from({
                length: CompanyService.getTotalPriorities() ?? 0
              }).map((_, i) => ({
                value: (i + 1).toString(),
                label: (i + 1).toString()
              }))}
            />
          </div>
        )}
        {filter &&
          [
            DataType.Text,
            DataType.Phone,
            DataType.Link,
            DataType.TextWithOperator
          ].includes(filter.kind) && (
            <>
              <SelectOperations
                operators={AdvancedFilterService.textWithOperatorOperators}
                value={filter.operator}
                filter={filter}
                setFilter={setFilterCombo}
              />
              {filter.operator !== 7 && (
                <div className="col-md-4">
                  <input
                    onChange={onChangeValueInput}
                    defaultValue={filter.value}
                    className="form-control"
                  />
                </div>
              )}
            </>
          )}
        {filter && DataType.ExpirationDate === filter.kind && (
          <>
            <SelectValue
              value={filter.value}
              keyValues={AdvancedFilterService.expirationDateOptionsUntranslated
                .map((x) => ({
                  Label: TranslationService.getTranslation(x.Label),
                  Value: x.Value
                }))
                .concat(
                  CompanyService.GetAgeingValues().map((x) => ({
                    Label: x.name,
                    Value: x.filterVal
                  }))
                )}
              onChangeValue={onChangeValue}
            />
          </>
        )}
        {filter && DataType.YesNo === filter.kind && (
          <>
            <SelectValue
              value={filter.value}
              keyValues={[
                { Value: "0", Label: translate.No },
                { Value: "1", Label: translate.Yes }
              ]}
              onChangeValue={onChangeValue}
            />
          </>
        )}
        {filter && filter.kind === DataType.Percentage && (
          <>
            <SelectOperations
              operators={AdvancedFilterService.percentageOperators}
              value={filter.operator}
              filter={filter}
              setFilter={setFilterCombo}
            />
            <div className="col">
              <TextEditorNumber
                onChange={onChangeValueInputText}
                defaultValue={filter.value}
              />
              {/* <input onChange={onChangeValueInput} className="form-control" type={"number"} defaultValue={filter.value} /> */}
            </div>
            <div className="col-auto">
              <input
                type="text"
                readOnly
                className="form-control-plaintext mt-1"
                value={"% " + translate.OfUsedCredit}
              />
            </div>
          </>
        )}
        {filter && DataType.Status === filter.kind && (
          <SelectValue
            value={filter.value}
            keyValues={filter.definition.AdditionalDefinitionItems}
            onChangeValue={onChangeValue}
          />
        )}
        {filter && DataType.Type === filter.kind && (
          <SelectValue
            value={filter.value}
            keyValues={typeFilters}
            onChangeValue={onChangeValue}
          />
        )}
        {filter && DataType.ActivityTypeWithDate === filter.kind && (
          <>
            <DateTextboxValue
              defaultValue={filter.value ?? null}
              relativeDays={filter.relativeDays}
              operator={filter.operator}
              onChangeValue={onChangeDateValue}
              onChangeRelativeDays={onChangeRelativeDays}
              small
            />
            <div className="col-md-2">
              <select
                className="form-control h-40px"
                value={
                  (filter as FilterActivityTypeWithDate).activityTypeFilter
                }
                onChange={(e) =>
                  onFilterChange("activityTypeFilter")(e.target.value)
                }
              >
                <option>{translate.All}</option>
                {activityTypeFilterItems.map(
                  (item: { Label: string; Value: string }) => (
                    <option key={item.Value} value={item.Value}>
                      {item.Label}
                    </option>
                  )
                )}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-control h-40px"
                value={(filter as FilterActivityTypeWithDate).userId}
                onChange={(e) => onFilterChange("userId")(e.target.value)}
              >
                <option>{translate.All}</option>
                {CompanyService.getUsers().map((item) => (
                  <option key={item.Id} value={item.Id}>
                    {item.Value}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>
      <div className="delete">
        <div className="delete">
          <button
            className="btn button-outline-primary"
            onClick={props.deleteFilter}
          >
            <i className="fa-light fa-times text-danger fa-lg"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

// Operator options for the relative-days control. The stored numbers are positions in
// `AdvancedFilterService.numericOperator`, which is what the backend already interprets
// in `selOperatorDate` — hence Greater is 2 and LessThan is 1, not the display order.
const RELATIVE_DAYS_OPERATORS = [
  { translationKey: "Equal" as const, value: 0 },
  { translationKey: "Greater" as const, value: 2 },
  { translationKey: "LessThan" as const, value: 1 }
];

const RELATIVE_DAYS_LIMIT = 365 * 25;

const DateTextboxValue = ({
  onChangeValue,
  onChangeRelativeDays,
  defaultValue,
  relativeDays,
  operator,
  small = false
}: {
  defaultValue?: string | null;
  relativeDays?: string;
  operator?: number;
  onChangeValue: (value: string) => void;
  onChangeRelativeDays: (days: string, operator: number) => void;
  small?: boolean;
}) => {
  const location = useLocation();
  // Value pushed INTO the datepicker from outside its own UI. `revision` makes the picker
  // re-read it even when the value repeats, so the two controls never drift apart.
  const [pickerValue, setPickerValue] = useState<{
    value: string | null;
    revision: number;
  }>(() => ({ value: defaultValue ?? null, revision: 0 }));
  const [daysInput, setDaysInput] = useState(relativeDays ?? "");
  const [daysOperator, setDaysOperator] = useState(
    operator ?? RELATIVE_DAYS_OPERATORS[0].value
  );

  const operators = useMemo(
    () =>
      RELATIVE_DAYS_OPERATORS.map((x) => ({
        text: TranslationService.getTranslation(x.translationKey),
        value: x.value
      })),
    []
  );

  const pushToDatepicker = (value: string | null) =>
    setPickerValue((prev) => ({ value, revision: prev.revision + 1 }));

  // Relative-days path. The days are sent to the backend as-is (`dateActivityFilter`);
  // nothing is computed into absolute dates here. Writing days clears the datepicker,
  // because the two controls are mutually exclusive.
  const applyDays = (days: string, op: number) => {
    onChangeRelativeDays(days, op);
    pushToDatepicker(null);
  };

  // Datepicker path: an explicit date supersedes the day count, so the days input is
  // cleared. Only reachable from user interaction — the picker stays silent when it
  // syncs from `pickerValue`, which is what keeps this from looping.
  const onChangeDate = (start?: Date | null, end?: Date | null) => {
    setDaysInput("");

    const s = start ?? end;
    const e = end ?? start;
    if (!s || !e) {
      onChangeValue("");
      return;
    }

    const valueStart = formatDate(s);
    const valueEnd = formatDate(e);
    onChangeValue(
      valueStart === valueEnd ? valueStart : `${valueStart}-${valueEnd}`
    );
  };

  const showDays = [
    "report/reportactivity",
    "report/ioactivity",
    "report/activitytypesummary",
    "ImportTemplateManual",
    "consolidated/report"
  ].some((x) => location.pathname.includes(x));

  return (
    <>
      <div className={"col"}>
        <RangedDatepicker
          onRangeChange={onChangeDate}
          defaultValue={pickerValue.value}
          syncToken={pickerValue.revision}
        />
      </div>
      {showDays && (
        <div className="col pe-0">
          <div className="row align-items-center">
            <div className="col-auto px-1 ps-2">|</div>
            <div className="col ps-0">
              <Dropdown
                items={operators}
                value={daysOperator}
                onChange={(op) => {
                  setDaysOperator(op);
                  // Only re-report if the days control is the one in use.
                  if (daysInput !== "") {
                    applyDays(daysInput, op);
                  }
                }}
              />
            </div>
            <div className="col">
              <NumberInput
                className="form-control d-block-inline"
                allowNegative
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  if (isNaN(parsed)) {
                    // Partial input ("", "-"): keep what was typed, report nothing yet.
                    setDaysInput(e.target.value);
                    if (e.target.value === "") {
                      onChangeRelativeDays("", daysOperator);
                    }
                    return;
                  }
                  const days = Math.max(
                    Math.min(parsed, RELATIVE_DAYS_LIMIT),
                    -RELATIVE_DAYS_LIMIT
                  ).toString();
                  setDaysInput(days);
                  applyDays(days, daysOperator);
                }}
                value={daysInput}
              />
            </div>
            <div className="col-auto p-0 ps-1">
              {TranslationService.translate.Days}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export type AdvancedFiltersValues = {
  show: boolean;
  setShow: (val: boolean) => void;
  filtersCount: number;
  setFiltersCount: (val: number) => void;
  filterSavedByPage: any[];
  setFilterSavedByPage: (val: any) => void;
  selectedFilterSaved: number;
  setSelectedFilterSaved: (val: number) => void;
  reportId?: ReportId;
  setReportId: (val: ReportId) => void;
  filtersSaved?: StoredFiltersResponse;
  setFiltersSaved: (val: StoredFiltersResponse | undefined) => void;
  isSaving: boolean;
  setIsSaving: (val: boolean) => void;
  firstSelection: boolean;
  setFirstSelection: (val: boolean) => void;
};
export const AdvancedFiltersContext = createContext(
  {} as AdvancedFiltersValues
);
export const AdvancedFiltersProvider = ({
  children
}: PropsWithChildren<Record<string, unknown>>) => {
  const [show, setShow] = useState(false);
  const [filtersCount, setFiltersCount] = useState(0);
  const [selectedFilterSaved, setSelectedFilterSaved] = useState<number>(-1);
  const [filterSavedByPage, setFilterSavedByPage] = useState<any[]>([]);
  const [reportId, setReportId] = useState<ReportId | undefined>();
  const [filtersSaved, setFiltersSaved] = useState<
    StoredFiltersResponse | undefined
  >(undefined);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [firstSelection, setFirstSelection] = useState<boolean>(true);

  return (
    <AdvancedFiltersContext.Provider
      value={{
        show,
        setShow,
        filtersCount,
        setFiltersCount,
        selectedFilterSaved,
        setSelectedFilterSaved,
        filterSavedByPage,
        setFilterSavedByPage,
        reportId,
        setReportId,
        filtersSaved,
        setFiltersSaved,
        isSaving,
        setIsSaving,
        firstSelection,
        setFirstSelection
      }}
    >
      {children}
    </AdvancedFiltersContext.Provider>
  );
};

export const AdvancedFiltersButton = ({
  savedFiltersId,
  minimal = false
}: {
  savedFiltersId?: any;
  minimal?: boolean;
}) => {
  const {
    setShow,
    show,
    setSelectedFilterSaved,
    selectedFilterSaved,
    setFilterSavedByPage,
    filterSavedByPage,
    reportId,
    setReportId,
    filtersSaved,
    setFiltersSaved,
    isSaving,
    setIsSaving,
    setFirstSelection
  } = useContext(AdvancedFiltersContext);

  const getSavedFilters = useCallback(
    async (cleanFilters?: boolean) => {
      setIsSaving(true);

      const response = StoredFiltersService.getListByReport({
        report: savedFiltersId
      });
      if (response instanceof Error) {
        console.error("Error");
      } else {
        setFiltersSaved(response);
        const list =
          response.list.map((el) => ({
            Value: el.Name,
            Id: el.StoredFilterID
          })) || [];
        list.unshift({ Value: TranslationService.translate.MyFilters, Id: -1 });
        setFilterSavedByPage([...list]);
        setFirstSelection(true);
        setIsSaving(false);
        cleanFilters && setSelectedFilterSaved(-1);
      }
    },
    [
      savedFiltersId,
      setFilterSavedByPage,
      setFiltersSaved,
      setFirstSelection,
      setIsSaving,
      setSelectedFilterSaved
    ]
  );

  useEffect(() => {
    if (savedFiltersId >= 0) {
      setReportId(savedFiltersId);
      getSavedFilters();
    }
  }, [getSavedFilters, savedFiltersId, setReportId]);

  const showFilterToEdit = async () => {
    const filter = filtersSaved?.list.find(
      (el) => el.StoredFilterID === selectedFilterSaved
    );
    if (filter && parseInt(savedFiltersId) >= 0) {
      await FloatingPanelService.showPanel({
        title: TranslationService.translate.StoredFilters,
        children: (
          <StoredFilters
            filter={filter}
            reportId={savedFiltersId}
            getSavedFilters={getSavedFilters}
          />
        ),
        height: 350,
        width: 600,
        position: "center"
      });
    }
  };

  const handleChangeSavedFilter = (el: number) => {
    setSelectedFilterSaved(el);
    setFirstSelection(true);
  };

  return (
    <>
      <span
        style={{
          gap: "12px",
          width: "auto",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          color: "var(--intiza-darkGrey2)"
        }}
      >
        <TooltipComponent title={TranslationService.translate.Filters}>
          <button
            onClick={() => setShow(!show)}
            type="button"
            className={
              minimal ? "btn text-primary" : "btn button-outline-primary"
            }
            style={
              minimal
                ? { border: "none", background: "transparent", padding: 0 }
                : undefined
            }
          >
            <i className={minimal ? "fa fa-filter" : "fal fa-bars-filter"} />
          </button>
        </TooltipComponent>
        {/* {filtersCount > 0 && <span className="ps-2">{TranslationService.translate.ActiveFilters}: {filtersCount}</span>} */}
        {!isSaving ? (
          <>
            {parseInt(reportId) >= 0 && filterSavedByPage.length > 1 ? (
              <>
                <Dropdown
                  items={filterSavedByPage}
                  defaultValue={selectedFilterSaved}
                  onChange={handleChangeSavedFilter}
                  value={selectedFilterSaved}
                  placeholder="Sin filtros"
                  noBorder={minimal}
                />
                {selectedFilterSaved && selectedFilterSaved >= 0 ? (
                  <div onClick={showFilterToEdit}>
                    <i className="far fa-pen pointer"></i>
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}
      </span>
    </>
  );
};

const StoredFilters = ({
  filter,
  reportId,
  getSavedFilters,
  isNew
}: {
  filter: Partial<StoredFilterItem>;
  reportId: ReportId;
  getSavedFilters?: (reload?: boolean, newId?: number) => void;
  isNew?: boolean;
}) => {
  const { isSaving, setIsSaving, setFirstSelection } = useContext(
    AdvancedFiltersContext
  );
  const { showToast } = useContext(ToastContext);
  const isInvited = CompanyService.getIsInvited();
  const [privateState, setPrivateState] = useState<boolean>(() => {
    // Si es invitado, siempre debe ser true (solo para mí)
    if (isInvited) {
      return true;
    }
    return filter.Private ?? false;
  });
  const [filterName, setFilterName] = useState(filter.Name);
  const [isDeleting, setIsDeleting] = useState(false);

  const requiredManager = new RequiredManager();

  // Actualizar el estado cuando cambie el filter prop
  useEffect(() => {
    // Si es invitado, siempre debe ser true (solo para mí)
    if (isInvited) {
      setPrivateState(true);
    } else {
      setPrivateState(filter.Private ?? false);
    }
  }, [filter.Private, isInvited]);

  const options = [
    { value: true, text: TranslationService.translate.OnlyMe },
    { value: false, text: TranslationService.translate.AllUsers }
  ];

  const updateSavedFilter = async () => {
    if (
      !requiredManager.validate() ||
      !filter.Filter ||
      !(parseInt(reportId) >= 0) ||
      !filterName
    ) {
      showToast(
        TranslationService.translate.MissingRequiredFields,
        "",
        "danger"
      );
      return;
    } else if (filterName?.length < 3) {
      showToast(TranslationService.translate.ErrorNameTooShort, "", "danger");
      return;
    } else {
      try {
        setIsSaving(true);
        const item: StoredFilterItem = {
          Report: reportId,
          Name: filterName!,
          UserID: filter.UserID || parseInt(CompanyService.getUserid()),
          Private: isInvited ? true : privateState, // Invitados siempre crean filtros solo para ellos
          Filter: filter.Filter!,
          StoredFilterID_ref: filter?.StoredFilterID_ref,
          StoredFilterID: filter?.StoredFilterID
        };

        const result = await StoredFiltersService.set({ item });
        if (result instanceof Error) {
          const error = getError(result);
          if (error.message === "Fail-Name") {
            ToastService.showToast(
              TranslationService.translate.FieldAlreadyCreated,
              undefined,
              "danger"
            );
          } else {
            ToastService.showToast(
              TranslationService.translate.ErrorProcessingRequest,
              undefined,
              "danger"
            );
          }
          return;
        }
        if (getSavedFilters) {
          await getSavedFilters(false, result.Id);
          await setFirstSelection(true);
          await FloatingPanelService.hidePanel();
        }
      } finally {
        setIsSaving(false);
      }
    }
  };

  const confirmDelete = () => {
    ModalService.showDefaultModal({
      acceptButtonClassName: "btn btn-danger",
      acceptButtonLabel: TranslationService.translate.Yes,
      message: TranslationService.translate.SureDeleteFilter,
      title: TranslationService.translate.Delete,
      onAcceptClick: deleteSavedFilter
    });
  };

  const deleteSavedFilter = useCallback(async () => {
    if (filter.StoredFilterID && getSavedFilters) {
      setIsDeleting(true);
      const result = await StoredFiltersService.delete({
        id: filter.StoredFilterID
      });
      if (result instanceof Error) {
        showToast(
          TranslationService.translate.ErrorProcessingRequest,
          "",
          "danger"
        );
      }
      setIsDeleting(false);
      getSavedFilters(true);
      FloatingPanelService.hidePanel();
    }
  }, [filter.StoredFilterID, getSavedFilters, showToast]);
  function getTranslation(reportId: ReportId) {
    const key = TYPE_REPORT[reportId];
    return key ? TranslationService.translate[key] : null;
  }
  return (
    <>
      <div className="tabBody">
        {!isNew ? (
          <div className="row mb-1">
            <label className="col-3 form-label">
              {TranslationService.translate.Type}
            </label>
            <div className="col">{getTranslation(reportId)}</div>
          </div>
        ) : null}

        <div className="row mb-3">
          <label className="col-3 font-weight-normal col-form-label">
            {TranslationService.translate.Name}:
          </label>
          <div className="col">
            <TextEditor
              onChange={requiredManager.makeRequiredWithId(
                setFilterName,
                "textId"
              )}
              defaultValue={filterName}
              maxLength={STORED_FILTER_NAME_MAX_LENGTH}
            />
            <ValidationMessage
              onChange={requiredManager.makeRequiredWithId(
                setFilterName,
                "textId"
              )}
              defaultValue={filterName}
            />
          </div>
        </div>

        {!isInvited && (
          <div className="row mb-3">
            <label className="col-3 font-weight-normal col-form-label">
              {TranslationService.translate.VisibleTo}:
            </label>
            <div className="col">
              <Dropdown
                value={privateState}
                onChange={setPrivateState}
                items={options}
              />
            </div>
          </div>
        )}
      </div>
      <FloatingPanelFooter
        showDelete={!isSaving}
        onDeleteClick={confirmDelete}
        isDeleting={isDeleting}
      >
        {!isDeleting ? (
          <button className="btn btn-primary" onClick={updateSavedFilter}>
            {TranslationService.translate.Save}
            {isSaving && (
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            )}
          </button>
        ) : null}
      </FloatingPanelFooter>
    </>
  );
};

const SelectOperations = ({
  value,
  operators,
  filter,
  setFilter,
  classCol = "col-md-4"
}: {
  value?: number;
  operators: readonly string[];
  filter: Filter;
  setFilter: (filter: Filter) => void;
  classCol?: string;
}) => {
  const onChange = (value: string) => {
    const newFilter = filter!;
    newFilter.operator = operators.indexOf(value);
    if (value === "Empty") {
      newFilter.value = "";
    }
    setFilter(newFilter);
  };
  return (
    <div className={classCol}>
      <Dropdown
        value={operators[value ?? 0]}
        onChange={onChange}
        items={operators
          .filter((x) => x)
          .map((x) => ({
            text: TranslationService.getTranslation(x),
            value: x
          }))}
      />
    </div>
  );
};

const MultipleSelectOperations = ({
  selectedValues,
  keyValues,
  setFilter,
  filter
}: {
  selectedValues?: { label: string; value: string }[];
  keyValues: { label: string; value: string }[];
  filter: Filter;
  setFilter: (filter: Filter) => void;
}) => {
  const { translate } = TranslationService;
  const [values, setValues] = useState(selectedValues);
  const onChange = (values: string[] | undefined) => {
    const newFilter = filter!;
    if (!values) {
      setFilter(newFilter);
      return;
    }

    setValues(values.map((x) => keyValues.find((y) => y.value === x)!));
    newFilter.value = values.join(",");
    setFilter(newFilter);
  };

  return (
    <div className="col-md-4">
      <MultiselectEditor
        placeholder={translate.Select}
        callback={onChange}
        value={values}
        items={keyValues.sort((a, b) => a.label.localeCompare(b.label))}
      />
    </div>
  );
};

const SelectValue = ({
  value,
  keyValues,
  onChangeValue
}: {
  value?: string;
  keyValues: { Label: string; Value: string }[];
  onChangeValue: (val: string) => void;
}) => {
  return (
    <div className="col-md-4">
      <Dropdown
        value={value}
        onChange={onChangeValue}
        items={[
          { text: TranslationService.translate.Select, value: "Empty" },
          ...keyValues.map((x) => ({ text: x.Label, value: x.Value }))
        ]}
      />
    </div>
  );
};

export default AdvancedFilters;
